import { AxiosError } from "axios";
import {
  Button,
  Container,
  Form,
  Grid,
  Input,
  Link,
  List,
  Message,
  RadioGroup,
  Select,
  Stack,
  Text,
} from "basis";
import { FormActions } from "components/FormActions/FormActions";
import { STATIC_RESOURCES } from "constants/contacts";
import { postDirectDebit } from "library/api";
import {
  validateAccountName,
  validateAccountNumber,
  validateBankName,
  validateBSB,
} from "library/formValidation";
import { Dispatch, useState } from "react";
import { useMutation } from "react-query";
import { CreditCard } from "../CreditCard/CreditCard";
import {
  BACK_CLICKED_WITH_FORM_VALUES,
  PAYMENT_TYPES,
  SET_DIRECT_DEBIT_FAILED,
  SET_DIRECT_DEBIT_SUCCEEDED,
} from "./constants";
import { paymentTypes } from "./directDebitReducer";
import { validatePaymentAmount } from "./directDebitUtils";
import { Action, FormValues, State, ToAccount } from "./types";

const { serviceAgreementUrl } = STATIC_RESOURCES;

export const jointAccountOptions = [
  {
    label: "No",
    value: "no",
  },
  {
    label: "Yes, I have sole authority to sign",
    value: "yes-sole",
  },
] as const;

interface Props {
  state: State;
  dispatch: Dispatch<Action>;
}

export const DirectDebitForm = ({ state, dispatch }: Props) => {
  const toAccount = state.toAccount as ToAccount;
  const {
    cardName,
    cardEndsWith,
    cardToken,
    westpacUserId,
    prfi = false,
  } = toAccount.data;
  const paymentTypesRadioOptions = paymentTypes.map((paymentType) =>
    paymentType.label === "Full statement closing balance"
      ? {
          ...paymentType,
          label: prfi
            ? "Payment to reduce future interest"
            : "Full statement closing balance",
          description: prfi
            ? "Includes all interest bearing purchases and interest free plans expiring each month"
            : "What you might like to pay to save on interest",
        }
      : paymentType,
  );

  const [submitError, setSubmitError] = useState();
  const setDirectDebitMutation = useMutation(postDirectDebit, {
    onSuccess: (
      _,
      {
        accountName,
        jointAccount,
        bankName,
        bsb,
        accountNumber,
        paymentType,
        paymentAmount,
      },
    ) => {
      dispatch({
        type: SET_DIRECT_DEBIT_SUCCEEDED,
        formValues: {
          accountName,
          jointAccount,
          bankName,
          bsb,
          accountNumber,
          paymentType,
          paymentAmount,
        },
      });
    },
    onError: (error: AxiosError, { setErrors }) => {
      const errorType = error.response?.data?.type ?? null;

      switch (errorType) {
        case "DDR_INVALID_BSB": {
          setErrors({
            bsb: "Invalid BSB",
          });
          setSubmitError(errorType);
          break;
        }

        case "DDR_NAME_MISMATCH": {
          setErrors({
            accountName: `Must match the name on your ${cardName} account.`,
          });
          setSubmitError(errorType);
          break;
        }

        default: {
          dispatch({ type: SET_DIRECT_DEBIT_FAILED, errorType });
        }
      }
    },
  });
  const onSubmit = ({ errors, values, setErrors }) => {
    const {
      accountName,
      jointAccount,
      bankName,
      bsb,
      accountNumber,
      paymentType,
      paymentAmount,
    } = values;

    if (Object.keys(errors).length === 0 && cardToken) {
      setDirectDebitMutation.mutate({
        token: cardToken,
        accountName,
        jointAccount,
        bankName,
        bsb,
        accountNumber,
        paymentType,
        setErrors,
        paymentAmount,
      });
    }
  };

  return (
    <Form initialValues={state.formValues} onSubmit={onSubmit}>
      {({ state: { values } }: { state: { values: FormValues } }) => (
        <Stack gap="8">
          <Grid cols="4" cols-sm="8" colsGap="30px" rowsGap="2">
            <Grid.Item colSpan="all" colSpan-sm="0-1">
              <Text>
                <strong>To account</strong>
              </Text>
            </Grid.Item>
            <Grid.Item colSpan="all" colSpan-sm="2-7">
              <Container bg="grey.t05" padding="4">
                <CreditCard cardName={cardName} cardEndsWith={cardEndsWith} />
              </Container>
            </Grid.Item>
          </Grid>
          <Grid cols="4" cols-sm="8" colsGap="30px" rowsGap="2">
            <Grid.Item colSpan="all" colSpan-sm="0-1">
              <Text>
                <strong>From account</strong>
              </Text>
            </Grid.Item>
            <Grid.Item colSpan="all" colSpan-sm="2-7">
              <Container>
                <Grid rowsGap="8">
                  <Input
                    name="accountName"
                    label="Name of account to be debited"
                    helpText="Must be a bank account in your name or a joint account that includes your name. Business accounts are not accepted."
                    validate={validateAccountName}
                  />
                  <Select
                    name="jointAccount"
                    label="Is this a joint account?"
                    placeholder="Select an option"
                    options={jointAccountOptions}
                  />
                  <Input
                    name="bankName"
                    label="Bank name"
                    validate={validateBankName}
                  />
                  <Container width="50%">
                    <Input
                      name="bsb"
                      label="BSB"
                      validate={validateBSB}
                      testId="bsb"
                    />
                  </Container>
                  <Input
                    variant="numeric"
                    name="accountNumber"
                    label="Account number"
                    validate={validateAccountNumber}
                  />
                </Grid>
              </Container>
            </Grid.Item>
          </Grid>
          <Grid cols="4" cols-sm="8" colsGap="30px" rowsGap="2">
            <Grid.Item colSpan="all" colSpan-sm="0-1">
              <Text>
                <strong>Monthly payment amount</strong>
              </Text>
            </Grid.Item>
            <Grid.Item colSpan="all" colSpan-sm="2-7">
              <RadioGroup
                testId="paymentType"
                name="paymentType"
                label=""
                options={paymentTypesRadioOptions}
                columns={1}
              />
              {values.paymentType === PAYMENT_TYPES.FIXED && (
                <Container width="50%" margin="6 0 0 0">
                  <Input
                    name="paymentAmount"
                    label="Fixed amount"
                    helpText="Minimum amount of $10."
                    validate={validatePaymentAmount}
                    variant="numeric"
                    prefix="$"
                  />
                </Container>
              )}
            </Grid.Item>
          </Grid>

          <Grid cols="4" cols-sm="8" colsGap="30px" rowsGap="2">
            <Grid.Item colSpan="all" colSpan-sm="0-1">
              <Text>
                <strong>Request confirmation</strong>
              </Text>
            </Grid.Item>
            <Grid.Item colSpan="all" colSpan-sm="2-7">
              <Container>
                <Text>By clicking 'I agree',</Text>
                <List margin="3 0 0 0">
                  <List.Item>
                    I confirm the above details are true and correct;
                  </List.Item>
                  <List.Item>
                    I authorise and request Latitude Financial Services (User
                    ID: {westpacUserId}) to arrange for the payments specified
                    above to be debited through the Bulk Electronic Clearing
                    System from my account listed above;
                  </List.Item>
                  <List.Item>
                    I agree to the terms and conditions of the{" "}
                    <Link href={serviceAgreementUrl} newTab>
                      Direct Debit Request Service Agreement
                    </Link>
                    .
                  </List.Item>
                </List>
              </Container>
            </Grid.Item>
          </Grid>
          {submitError === "DDR_INVALID_BSB" && (
            <Grid cols="4" cols-sm="8" colsGap="30px">
              <Grid.Item colSpan="all" colSpan-sm="2-7">
                <Message
                  severity="blocking"
                  bg="secondary.pink.t30"
                  title="BSB is not recognised"
                >
                  Please check your bank details and enter a valid BSB.
                </Message>
              </Grid.Item>
            </Grid>
          )}
          {submitError === "DDR_NAME_MISMATCH" && (
            <Grid cols="4" cols-sm="8" colsGap="30px">
              <Grid.Item colSpan="all" colSpan-sm="2-7">
                <Message
                  severity="blocking"
                  bg="secondary.pink.t30"
                  title="Account names don't match"
                >
                  The name of the account to be debited must match the name on
                  your {cardName} account.
                </Message>
              </Grid.Item>
            </Grid>
          )}
          <Grid cols="4" cols-sm="8" colsGap="30px">
            <Grid.Item colSpan="all" colSpan-sm="2-7">
              <Container width="100%" width-sm="360">
                <FormActions>
                  <FormActions.Primary>
                    <Button
                      type="submit"
                      width="100%"
                      loading={setDirectDebitMutation.isLoading}
                    >
                      I agree
                    </Button>
                  </FormActions.Primary>
                  <FormActions.Secondary>
                    <Button
                      variant="secondary"
                      width="100%"
                      onClick={() => {
                        dispatch({
                          type: BACK_CLICKED_WITH_FORM_VALUES,
                          formValues: values,
                        });
                      }}
                    >
                      Back
                    </Button>
                  </FormActions.Secondary>
                </FormActions>
              </Container>
            </Grid.Item>
          </Grid>
        </Stack>
      )}
    </Form>
  );
};
